// Short-form Web Speech dictation. Chrome ends a "continuous" session
// after a pause and often re-sends prior finals — rebuild each event
// from all results and restart until the operator hits Stop.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  foldSpeechRecognitionResults,
  liveDictationDraft,
  mergeDictationUtterance,
  collapseRepeatedTranscript,
} from "@/lib/speech/speechDictationTranscript";

type DictationState = "idle" | "listening" | "stopped" | "unsupported" | "error";

type SpeechRecognitionCtor = new () => SpeechRecognition;

const RESTART_MS = 250;
const MAX_START_FAILS = 4;

function resultsFromEvent(
  evt: SpeechRecognitionEvent,
): Array<{ isFinal: boolean; transcript: string }> {
  const out: Array<{ isFinal: boolean; transcript: string }> = [];
  for (let i = 0; i < evt.results.length; i++) {
    const res = evt.results[i];
    out.push({
      isFinal: Boolean(res?.isFinal),
      transcript: res?.[0]?.transcript ?? "",
    });
  }
  return out;
}

export function useSpeechDictation() {
  const [state, setState] = useState<DictationState>("idle");
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);

  const recRef = useRef<SpeechRecognition | null>(null);
  const wantListeningRef = useRef(false);
  const sessionFinalRef = useRef("");
  const restartTimerRef = useRef<number | null>(null);
  const beginRef = useRef<() => void>(() => {});
  const genRef = useRef(0);
  const startFailRef = useRef(0);

  const RecognitionCtor: SpeechRecognitionCtor | null = useMemo(() => {
    const w = window as Window & {
      SpeechRecognition?: SpeechRecognitionCtor;
      webkitSpeechRecognition?: SpeechRecognitionCtor;
    };
    return w.SpeechRecognition || w.webkitSpeechRecognition || null;
  }, []);

  const clearRestartTimer = useCallback(() => {
    if (restartTimerRef.current != null) {
      window.clearTimeout(restartTimerRef.current);
      restartTimerRef.current = null;
    }
  }, []);

  const detachRec = useCallback((rec: SpeechRecognition) => {
    rec.onstart = null;
    rec.onresult = null;
    rec.onerror = null;
    rec.onend = null;
  }, []);

  const scheduleRestart = useCallback((delayMs: number) => {
    clearRestartTimer();
    restartTimerRef.current = window.setTimeout(() => {
      restartTimerRef.current = null;
      if (wantListeningRef.current) beginRef.current();
    }, delayMs);
  }, [clearRestartTimer]);

  const beginRecognition = useCallback(() => {
    if (!RecognitionCtor || !wantListeningRef.current) return;
    clearRestartTimer();
    const gen = ++genRef.current;
    const prev = recRef.current;
    if (prev) {
      detachRec(prev);
      try {
        prev.abort();
      } catch {
        /* intentional */
      }
      recRef.current = null;
    }

    const rec = new RecognitionCtor();
    recRef.current = rec;
    rec.lang = "en-US";
    rec.interimResults = true;
    rec.continuous = true;
    rec.maxAlternatives = 1;
    let instanceFinals = "";

    rec.onstart = () => {
      if (gen !== genRef.current) return;
      startFailRef.current = 0;
      setError(null);
      setState("listening");
    };

    rec.onresult = (evt: SpeechRecognitionEvent) => {
      if (gen !== genRef.current) return;
      const { finals, interim } = foldSpeechRecognitionResults(resultsFromEvent(evt));
      instanceFinals = finals;
      setDraft(liveDictationDraft(sessionFinalRef.current, finals, interim));
    };

    rec.onerror = (e: SpeechRecognitionErrorEvent) => {
      if (gen !== genRef.current) return;
      const code = String(e?.error ?? "");
      if (code === "aborted" || code === "no-speech") return;
      // Chrome often fires `network` when a phrase ends; onend still restarts.
      if (code === "network" && wantListeningRef.current) return;
      wantListeningRef.current = false;
      clearRestartTimer();
      setState("error");
      setError(code || "Dictation error");
    };

    rec.onend = () => {
      if (gen !== genRef.current) return;
      if (recRef.current !== rec) return;
      recRef.current = null;
      if (instanceFinals) {
        sessionFinalRef.current = collapseRepeatedTranscript(
          mergeDictationUtterance(sessionFinalRef.current, instanceFinals),
        );
      }
      setDraft(sessionFinalRef.current);
      if (!wantListeningRef.current) {
        setState("stopped");
        return;
      }
      scheduleRestart(RESTART_MS);
    };

    try {
      rec.start();
    } catch {
      recRef.current = null;
      if (!wantListeningRef.current) return;
      startFailRef.current += 1;
      if (startFailRef.current > MAX_START_FAILS) {
        wantListeningRef.current = false;
        setState("error");
        setError("Failed to start dictation");
        return;
      }
      scheduleRestart(RESTART_MS + startFailRef.current * 150);
    }
  }, [RecognitionCtor, clearRestartTimer, detachRec, scheduleRestart]);

  beginRef.current = beginRecognition;

  useEffect(() => {
    return () => {
      wantListeningRef.current = false;
      genRef.current += 1;
      clearRestartTimer();
      const rec = recRef.current;
      if (!rec) return;
      detachRec(rec);
      try {
        rec.abort();
      } catch {
        /* intentional */
      }
      recRef.current = null;
    };
  }, [clearRestartTimer, detachRec]);

  const start = useCallback(() => {
    setError(null);
    if (!RecognitionCtor) {
      setState("unsupported");
      return;
    }
    wantListeningRef.current = true;
    startFailRef.current = 0;
    sessionFinalRef.current = "";
    setDraft("");
    setState("listening");
    beginRecognition();
  }, [RecognitionCtor, beginRecognition]);

  const stop = useCallback(() => {
    wantListeningRef.current = false;
    clearRestartTimer();
    const rec = recRef.current;
    if (!rec) {
      setState("stopped");
      return;
    }
    try {
      rec.stop();
    } catch {
      setState("stopped");
    }
  }, [clearRestartTimer]);

  const reset = useCallback(() => {
    wantListeningRef.current = false;
    clearRestartTimer();
    sessionFinalRef.current = "";
    setDraft("");
    setError(null);
    setState("idle");
  }, [clearRestartTimer]);

  return {
    isSupported: !!RecognitionCtor,
    state,
    isListening: state === "listening",
    draft,
    error,
    start,
    stop,
    reset,
  };
}
